import React, { useMemo } from 'react';
import Plot from 'react-plotly.js';
import { useData } from '../../context/DataContext';
import { computeStripData } from '../../utils/stripPlotData';
import {
  STRIP_GROUP_ORDER, STRIP_GROUP_LABELS, GROUP_MARKERS, GROUP_SYMBOLS,
  IMMGEN_COLORS,
} from '../../utils/colors';

const nGroups = STRIP_GROUP_ORDER.length;
const tickVals = [...Array(nGroups).keys()];

export function SingleGeneStrip({ gene, perSample, expression9col, width, height }) {
  const result = computeStripData({ gene, perSample, expression9col });

  if (!result) {
    return (
      <div style={{ width, height, display: 'flex', alignItems: 'center',
                     justifyContent: 'center', color: '#666' }}>
        {gene} — not found
      </div>
    );
  }

  const { traces, shapes, yRange } = result;

  return (
    <Plot
      data={traces}
      layout={{
        width, height,
        paper_bgcolor: 'transparent',
        plot_bgcolor: '#fff',
        margin: { l: 40, r: 25, t: 30, b: 50 },
        title: { text: `<i>${gene}</i>`, font: { size: 13, color: '#1a1a1a', family: 'Inter' }, y: 0.95 },
        xaxis: {
          tickvals: tickVals,
          ticktext: STRIP_GROUP_LABELS,
          tickfont: { size: 7, color: '#555' },
          range: [-0.5, nGroups - 0.5],
          zeroline: false,
          fixedrange: true,
          showline: true,
          linecolor: '#ccc',
        },
        yaxis: {
          title: { text: 'log₂(TPM+1)', font: { size: 9, color: '#777' } },
          tickfont: { size: 8, color: '#555' },
          range: yRange,
          gridcolor: '#eee',
          zeroline: false,
          fixedrange: true,
          showline: true,
          linecolor: '#ccc',
        },
        shapes,
        hovermode: 'closest',
      }}
      config={{ displayModeBar: false }}
    />
  );
}

export default function StripPlot({ panelName }) {
  const { data } = useData();

  const geneList = useMemo(() => {
    if (!data) return [];
    if (!panelName) return data.stripGenes || [];
    const panel = data.panels[panelName];
    if (!panel) return data.stripGenes || [];
    const genes = [];
    for (const subcatGenes of Object.values(panel)) {
      genes.push(...subcatGenes);
    }
    return genes;
  }, [data, panelName]);

  if (!data) return null;

  const nGenes = geneList.length;
  const cols = nGenes <= 4 ? 2 : nGenes <= 9 ? 3 : 4;
  const cellW = Math.max(260, Math.min(320, 1000 / cols));
  const cellH = 230;

  return (
    <div className="strip-plot-container">
      <div className="strip-grid" style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${cols}, ${cellW}px)`,
        gap: '4px',
        justifyContent: 'center',
      }}>
        {geneList.map(gene => (
          <SingleGeneStrip
            key={gene}
            gene={gene}
            perSample={data.perSample}
            expression9col={data.expression}
            width={cellW}
            height={cellH}
          />
        ))}
      </div>
      {/* Legend */}
      <div className="strip-legend">
        <div className="legend-row">
          <span className="legend-section-label">Samples:</span>
          {STRIP_GROUP_ORDER.map(g => (
            <span key={g} className="legend-item">
              <span className="legend-marker" style={{ color: GROUP_MARKERS[g].color }}>
                {GROUP_SYMBOLS[g]}
              </span>
              {' '}{GROUP_MARKERS[g].name}
            </span>
          ))}
        </div>
        <div className="legend-row">
          <span className="legend-section-label">ImmGen ref:</span>
          {Object.entries(IMMGEN_COLORS).map(([label, color]) => (
            <span key={label} className="legend-item">
              <span className="legend-dash" style={{ borderColor: color }} />
              <span>{label} mean</span>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
